/**
 * StockRiskAlertsTable
 * Tabela paginada de alertas de ruptura.
 */

import React, { useEffect, useRef } from 'react'
import { Loader2, ChevronLeft, ChevronRight, WandSparkles } from 'lucide-react'
import './StockRiskAlertsTable.css'

const RISK_LABELS = {
  critical: 'Crítico',
  high: 'Alto',
  medium: 'Médio'
}

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0)
}

function StockRiskAlertsTable({
  items = [],
  loading = false,
  pagination,
  onPageChange,
  selectedIds = [],
  onSelectionChange,
  onAnalyzeProduct
}) {
  const selectAllRef = useRef(null)

  const { page = 1, totalPages = 1, total = 0 } = pagination || {}
  const selectedSet = new Set(selectedIds)
  const pageIds = items.map((item) => item.productId)
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedSet.has(id))
  const somePageSelected = pageIds.some((id) => selectedSet.has(id)) && !allPageSelected

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = somePageSelected
    }
  }, [somePageSelected, page, items])

  const toggleRow = (productId) => {
    if (!onSelectionChange) return
    if (selectedSet.has(productId)) {
      onSelectionChange(selectedIds.filter((id) => id !== productId))
    } else {
      onSelectionChange([...selectedIds, productId])
    }
  }

  const toggleAllOnPage = () => {
    if (!onSelectionChange) return
    if (allPageSelected) {
      onSelectionChange(selectedIds.filter((id) => !pageIds.includes(id)))
      return
    }
    onSelectionChange([...new Set([...selectedIds, ...pageIds])])
  }

  if (loading && items.length === 0) {
    return (
      <div className="stock-risk-table-loading">
        <Loader2 size={28} className="stock-risk-table-spinner" />
        <span>Carregando alertas...</span>
      </div>
    )
  }

  if (!loading && items.length === 0) {
    return (
      <div className="stock-risk-table-empty">
        <p>Nenhum alerta encontrado com os filtros atuais.</p>
      </div>
    )
  }

  return (
    <div className="stock-risk-table-wrap">
      <div className="stock-risk-table-scroll">
        <table className="stock-risk-table">
          <thead>
            <tr>
              {onSelectionChange && (
                <th className="stock-risk-table-check-col">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={toggleAllOnPage}
                    aria-label="Selecionar todos desta página"
                    title="Selecionar todos desta página"
                  />
                </th>
              )}
              <th>Produto</th>
              <th>Marca</th>
              <th>Disponível</th>
              <th>Mínimo</th>
              <th>Sugerido</th>
              <th>Valor est.</th>
              <th>Risco</th>
              {onAnalyzeProduct && <th className="stock-risk-table-action-col">IA</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.productId}>
                {onSelectionChange && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedSet.has(item.productId)}
                      onChange={() => toggleRow(item.productId)}
                      aria-label={`Selecionar ${item.name}`}
                    />
                  </td>
                )}
                <td>
                  <div className="stock-risk-table-product">
                    <span className="stock-risk-table-product-name">{item.name}</span>
                    {item.sku && (
                      <span className="stock-risk-table-product-sku">{item.sku}</span>
                    )}
                  </div>
                </td>
                <td>{item.brand || '—'}</td>
                <td className="stock-risk-table-num">{item.available}</td>
                <td className="stock-risk-table-num">{item.minStock}</td>
                <td className="stock-risk-table-num">{item.suggestedQty}</td>
                <td className="stock-risk-table-num">{formatCurrency(item.suggestedValue)}</td>
                <td>
                  <span className={`stock-risk-badge stock-risk-badge--${item.riskLevel}`}>
                    {RISK_LABELS[item.riskLevel] || item.riskLevel}
                  </span>
                </td>
                {onAnalyzeProduct && (
                  <td>
                    <button
                      type="button"
                      className="stock-risk-table-ai-btn"
                      title="Analisar com IA"
                      onClick={() => onAnalyzeProduct([item.productId])}
                    >
                      <WandSparkles size={14} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(totalPages > 1 || selectedIds.length > 0) && (
        <div className="stock-risk-table-pagination">
          <span className="stock-risk-table-pagination-info">
            {totalPages > 1
              ? `Página ${page} de ${totalPages} · ${total} itens`
              : `${total} itens`}
            {selectedIds.length > 0 && ` · ${selectedIds.length} selecionado(s)`}
          </span>
          {totalPages > 1 && (
            <div className="stock-risk-table-pagination-actions">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => onPageChange?.(page - 1)}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                disabled={page >= totalPages || loading}
                onClick={() => onPageChange?.(page + 1)}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default StockRiskAlertsTable
